import { apiGet, api } from './api';

export type PremiumFeatureType = 'EXTRA_GROUP_SLOTS' | 'EXTENDED_GROUP_SIZE' | 'CHAT_ENHANCEMENTS';

export interface UserPremiumFeatures {
  extraGroupSlots: boolean;
  chatEnhancements: boolean;
  premiumExpiresAt: string | null;
}

export interface CanJoinGroupResult {
  canJoin: boolean;
  reason?: string;
  requiresUpgrade?: boolean;
  upgradeType?: PremiumFeatureType;
  price?: number;
}

export interface CanExpandGroupResult {
  canExpand: boolean;
  reason?: string;
  requiresUpgrade?: boolean;
  upgradeType?: PremiumFeatureType;
  price?: number;
  currentCapacity?: number;
  maxCapacity?: number;
  maxPossibleCapacity?: number;
}

export interface PremiumPurchase {
  id: number;
  userId: number;
  featureType: PremiumFeatureType;
  targetId?: number;
  amount: number;
  currency: string;
  status: 'PENDING' | 'COMPLETED' | 'FAILED' | 'REFUNDED';
  paymentId?: string;
  quantity?: number;
  createdAt: string;
}

export interface PurchaseFeatureRequest {
  featureType: PremiumFeatureType;
  groupId?: number;
  quantity?: number;
}

export interface GroupUpgradeOption {
  quantity: number;
  capacityIncrease: number;
  newCapacity: number;
  cost: number;
  description: string;
}

export interface GroupUpgradeOptions {
  currentCapacity: number;
  maxPossibleCapacity: number;
  availableIncrements: number;
  upgradeOptions: GroupUpgradeOption[];
}

export interface UserGroupSlotUpgradeOption {
  quantity: number;
  slotsIncrease: number;
  newLimit: number;
  cost: number;
  description: string;
}

export interface UserGroupSlotUpgradeOptions {
  currentLimit: number;
  maxPossibleLimit: number;
  availableUpgrades: number;
  upgradeOptions: UserGroupSlotUpgradeOption[];
}

class PremiumService {
  /**
   * Get user's premium features
   */
  async getUserFeatures(): Promise<UserPremiumFeatures> {
    const response = await apiGet<UserPremiumFeatures>('/premium/features');
    return response;
  }

  /**
   * Check if user can join more groups
   */
  async canJoinGroup(): Promise<CanJoinGroupResult> {
    const response = await apiGet<CanJoinGroupResult>('/premium/can-join-group');
    return response;
  }

  /**
   * Check if group can be expanded
   */
  async canExpandGroup(groupId: number): Promise<CanExpandGroupResult> {
    const response = await apiGet<CanExpandGroupResult>(`/premium/can-expand-group/${groupId}`);
    return response;
  }

  /**
   * Check if user can create more groups
   */
  async canCreateGroup(): Promise<CanJoinGroupResult> {
    try {
      const response = await apiGet<CanJoinGroupResult>('/premium/can-create-group'); 
      return response;
    } catch (error) {
      console.error('❌ [PremiumService] API call failed:', error);
      throw error;
    }
  }

  /**
   * Get available upgrade options for a group
   */
  async getGroupUpgradeOptions(groupId: number): Promise<GroupUpgradeOptions> {
    const response = await apiGet<GroupUpgradeOptions>(`/premium/group-upgrade-options/${groupId}`);
    return response;
  }

  /**
   * Get available group slot upgrade options for current user
   */
  async getUserGroupSlotUpgradeOptions(): Promise<UserGroupSlotUpgradeOptions> {
    const response = await apiGet<UserGroupSlotUpgradeOptions>('/premium/user-group-slot-upgrade-options');
    return response;
  }

  /**
   * Purchase/activate premium feature (test mode)
   */
  async purchaseFeature(request: PurchaseFeatureRequest): Promise<{ success: boolean; message: string }> {
    
    try {
      // Use raw API call to get full response including success and message
      const fullResponse = await api.post('/premium/purchase', request);
      const response = fullResponse.data;
      
      return {
        success: response.success || false,
        message: response.message || 'Purchase completed'
      };
    } catch (error) {
      console.error('❌ [PremiumService] Purchase API error:', error);
      throw error;
    }
  }

  /**
   * Get user's purchase history
   */
  async getPurchaseHistory(): Promise<PremiumPurchase[]> {
    const response = await apiGet<PremiumPurchase[]>('/premium/purchases');
    return response;
  }

  /**
   * Get feature display name in Dutch
   */
  getFeatureDisplayName(feature: PremiumFeatureType): string {
    switch (feature) {
      case 'EXTRA_GROUP_SLOTS':
        return 'Extra groep slots';
      case 'EXTENDED_GROUP_SIZE':
        return 'Groep uitbreiding';
      case 'CHAT_ENHANCEMENTS':
        return 'Chat uitbreidingen';
      default:
        return feature;
    }
  }

  /**
   * Get feature description in Dutch
   */
  getFeatureDescription(feature: PremiumFeatureType): string {
    switch (feature) {
      case 'EXTRA_GROUP_SLOTS':
        return 'Join meer groepen tegelijkertijd (+3 slots per keer)';
      case 'EXTENDED_GROUP_SIZE':
        return 'Vergroot je groep met 5 extra plekken (max 50 leden)';
      case 'CHAT_ENHANCEMENTS':
        return 'Gebruik GIFs in de groep chat';
      default:
        return 'Premium feature';
    }
  }
}

export const premiumService = new PremiumService();